import React, { useMemo, useState } from 'react';
import { Stack, Group, Box, Button, Card, Switch, Text, Image } from '@mantine/core';
import { LayoutGrid } from 'lucide-react';
import { LoanDetailModal } from '../components/LoanDetailModal';
import { RuleViolationsAlert } from '../components/RuleViolationsAlert';
import { MetricsPanel } from '../components/MetricsPanel';
import { AdvancedFilterPanel } from '../components/AdvancedFilterPanel';
import { UnifiedLoanGrid } from '../components/UnifiedLoanGrid';
import { LoanKanbanBoard } from '../components/LoanKanbanBoard';
import { BulkActionToolbar } from '../components/BulkActionToolbar';
import { LOBoardView } from '../components/LOBoardView';
import { mockLoans } from '../data/mockLoans';
import { pipelineRules } from '../data/mockRules';
import { Loan } from '../types/loan';
import { RuleViolation } from '../types/rule';
export function LoanPipeline() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [loans, setLoans] = useState(mockLoans);
  const [activeRuleId, setActiveRuleId] = useState<string | null>(null);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedLoanOfficers, setSelectedLoanOfficers] = useState<string[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>([]);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [groupByStage, setGroupByStage] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'board' | 'lo-board'>('lo-board');
  // Extract unique loan officers and divisions
  const {
    loanOfficers,
    divisions
  } = useMemo(() => {
    const allLoans = Object.values(loans).flat();
    const loSet = new Set(allLoans.map(loan => loan.loanOfficer).filter(Boolean) as string[]);
    const divisionSet = new Set(allLoans.map(loan => loan.division));
    return {
      loanOfficers: Array.from(loSet).sort(),
      divisions: Array.from(divisionSet).sort()
    };
  }, [loans]);
  // Calculate rule violations
  const violations: RuleViolation[] = useMemo(() => {
    return pipelineRules.map(rule => {
      const affectedLoanIds: string[] = [];
      Object.entries(loans).forEach(([section, sectionLoans]) => {
        sectionLoans.forEach(loan => {
          if (rule.check(loan, section)) {
            affectedLoanIds.push(loan.id);
          }
        });
      });
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        severity: rule.severity,
        affectedLoanIds,
        description: rule.description
      };
    }).filter(v => v.affectedLoanIds.length > 0);
  }, [loans]);
  // Filter loans
  const filteredLoans = useMemo(() => {
    let result = {
      ...loans
    };
    // Apply stage filter
    if (selectedStages.length > 0) {
      Object.keys(result).forEach(section => {
        if (!selectedStages.includes(section)) {
          result[section] = [];
        }
      });
    }
    // Apply loan officer filter
    if (selectedLoanOfficers.length > 0) {
      Object.keys(result).forEach(section => {
        result[section] = result[section].filter(loan => selectedLoanOfficers.includes(loan.loanOfficer || ''));
      });
    }
    // Apply division filter
    if (selectedDivisions.length > 0) {
      Object.keys(result).forEach(section => {
        result[section] = result[section].filter(loan => selectedDivisions.includes(loan.division));
      });
    }
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      Object.keys(result).forEach(section => {
        result[section] = result[section].filter(loan => loan.clientName.toLowerCase().includes(query) || loan.loanNumber.includes(query) || loan.notes.toLowerCase().includes(query) || loan.lender.toLowerCase().includes(query));
      });
    }
    // Apply rule filter
    if (activeRuleId) {
      const activeViolation = violations.find(v => v.ruleId === activeRuleId);
      if (activeViolation) {
        Object.keys(result).forEach(section => {
          result[section] = result[section].filter(loan => activeViolation.affectedLoanIds.includes(loan.id));
        });
      }
    }
    return result;
  }, [loans, selectedStages, selectedLoanOfficers, selectedDivisions, searchQuery, activeRuleId, violations]);
  const handleLoanUpdate = (updatedLoan: Loan) => {
    const newLoans = {
      ...loans
    };
    Object.keys(newLoans).forEach(section => {
      newLoans[section] = newLoans[section].map(l => l.id === updatedLoan.id ? updatedLoan : l);
    });
    setLoans(newLoans);
    setSelectedLoan(updatedLoan);
  };
  const handleRuleSelect = (violation: RuleViolation | null) => {
    setActiveRuleId(violation?.ruleId || null);
  };
  const handleClearAllFilters = () => {
    setSelectedStages([]);
    setSelectedLoanOfficers([]);
    setSelectedDivisions([]);
    setSearchQuery('');
    setActiveRuleId(null);
  };
  return <Box style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0c1524 0%, #0f1d2f 40%, #162236 100%)'
  }} data-id="element-556">
      {/* Header */}
      <Box style={{
      background: 'linear-gradient(90deg, #0c1524 0%, #132035 100%)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
    }} data-id="element-557">
        <Box style={{
        maxWidth: 2000,
        margin: '0 auto',
        padding: '1rem 1.5rem'
      }} data-id="element-558">
          <Group justify="space-between" data-id="element-559">
            <Group gap="xl" data-id="element-560">
              <Image src="https://cdn.magicpatterns.com/uploads/fEsjb46Mxvne9mUXSX6NHT/nmhl_transparent.png" h={40} w="auto" fit="contain" alt="NMHL - National Mortgage Home Loans" data-id="element-561" />
              <div style={{
              borderLeft: '1px solid rgba(255, 255, 255, 0.15)',
              height: '32px'
            }} data-id="element-562" />
              <Text size="xl" fw={800} style={{
              letterSpacing: '-0.025em',
              color: 'rgba(255, 255, 255, 0.9)'
            }} data-id="element-563">
                Loan Pipeline
              </Text>
            </Group>

            <Group gap="md" data-id="element-564">
              <Group style={{
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              padding: '4px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }} data-id="element-565">
                <Button variant={viewMode === 'board' ? 'filled' : 'subtle'} size="sm" onClick={() => setViewMode('board')} styles={{
                root: {
                  backgroundColor: viewMode === 'board' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  color: viewMode === 'board' ? '#60a5fa' : 'rgba(255, 255, 255, 0.6)',
                  '&:hover': {
                    backgroundColor: viewMode === 'board' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)'
                  }
                }
              }} data-id="element-566">
                  Board View
                </Button>
                <Button variant={viewMode === 'table' ? 'filled' : 'subtle'} size="sm" onClick={() => setViewMode('table')} styles={{
                root: {
                  backgroundColor: viewMode === 'table' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                  color: viewMode === 'table' ? '#60a5fa' : 'rgba(255, 255, 255, 0.6)',
                  '&:hover': {
                    backgroundColor: viewMode === 'table' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)'
                  }
                }
              }} data-id="element-567">
                  Table View
                </Button>
              </Group>

              {viewMode === 'table' && <Switch label="Group by Stage" checked={groupByStage} onChange={e => setGroupByStage(e.currentTarget.checked)} color="blue" size="md" styles={{
              label: {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }} data-id="element-568" />}
            </Group>
          </Group>
        </Box>
      </Box>

      {/* Main Content */}
      <Box style={{
      maxWidth: 2000,
      margin: '0 auto',
      padding: '1.5rem'
    }} data-id="element-569">
        <Stack gap="lg" data-id="element-570">
          {/* Metrics Panel */}
          <MetricsPanel loans={filteredLoans} violations={violations} data-id="element-571" />

          {/* Rule Violations Alert */}
          <RuleViolationsAlert violations={violations} onSelectRule={handleRuleSelect} activeRuleId={activeRuleId} data-id="element-572" />

          {/* Advanced Filters */}
          <AdvancedFilterPanel searchQuery={searchQuery} onSearchChange={setSearchQuery} selectedStages={selectedStages} onStagesChange={setSelectedStages} selectedLoanOfficers={selectedLoanOfficers} onLoanOfficersChange={setSelectedLoanOfficers} selectedDivisions={selectedDivisions} onDivisionsChange={setSelectedDivisions} loanOfficers={loanOfficers} divisions={divisions} onClearAll={handleClearAllFilters} isOpen={filterPanelOpen} onToggle={() => setFilterPanelOpen(!filterPanelOpen)} data-id="element-573" />

          {/* Data Grid / Board */}
          {viewMode === 'board' ? <LOBoardView loans={filteredLoans} onLoanClick={setSelectedLoan} data-id="element-574" /> : <Box style={{
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }} data-id="element-575">
              <BulkActionToolbar selectedCount={selectedLoanIds.length} onClearSelection={() => setSelectedLoanIds([])} onExport={() => console.log('Export', selectedLoanIds)} data-id="element-576" />
              <UnifiedLoanGrid loans={filteredLoans} selectedLoanIds={selectedLoanIds} onSelectionChange={setSelectedLoanIds} onLoanClick={setSelectedLoan} groupByStage={groupByStage} data-id="element-577" />
            </Box>}
        </Stack>
      </Box>

      {/* Detail Modal */}
      <LoanDetailModal loan={selectedLoan} opened={!!selectedLoan} onClose={() => setSelectedLoan(null)} onUpdate={handleLoanUpdate} data-id="element-578" />
    </Box>;
}